import { tracking } from '@iwoca/frontend-tracking-library';
import { stringify } from 'qs';
import { getValues } from 'redux-form';
import { fork, takeLatest, select, put, take } from 'redux-saga/effects';

import {
    CHECK_ELIGIBILITY_STEP_ID,
    convertActiveStepIdToStepPathName
} from '@src/containers/Routes/helper/stepActiveIds.helper';
import { getQueryParams } from '@src/helpers/helpers';
import { updateSplitTestAttributes } from '@src/sagas';

import {
    ACTIVATE_BUSINESS_INTRODUCTION,
    SUBMIT_BUSINESS_INTRODUCTION
} from './BusinessIntroduction.actions';
import { nextStep } from '../../actions';
import { saveCustomerStateEdge } from '../../lendingAPI/helpers/saveCustomerStateEdge';
import { GET_CUSTOMER_STATE_EDGE } from '../../lendingAPI/lendingAPI.actions';
import { putAPI } from '../../lendingAPI/lendingAPIHelpers';
import {
    getCustomerStatus,
    hasAutomatedDecision,
    getCountryCode
} from '../../lendingAPI/lendingAPISelector';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';
import Tracking from '../../tracking/IwTracking';
import { navigateTo } from '../Routes/Navigation.action';
import { getBasePath } from '../Routes/Navigation.selector';

const CURRENT_STEP_ID = 'businessIntroduction';

export default [
    fork(businessIntroductionViewWorker),
    fork(submitBusinessIntroductionWorker)
];

function* businessIntroductionViewWorker() {
    yield takeLatest(ACTIVATE_BUSINESS_INTRODUCTION, function* () {
        const { customerStatus } = yield select((state) => ({
            customerStatus: getCustomerStatus(state)
        }));

        if (customerStatus === 'new') {
            yield put({ type: 'APPLICATION_STATE_FETCH_REQUESTED' });
            yield take('INITIAL_APP_END');
        }

        // This endpoint is called here so as to be restricted to step 1 only at this point
        // In future, when we move to only calling the Edge State endpoint, this can be integrated into the main flow
        yield putAPI({ type: `api/${GET_CUSTOMER_STATE_EDGE}` });
    });
}

function* submitBusinessIntroductionWorker() {
    yield takeLatest(SUBMIT_BUSINESS_INTRODUCTION, function* () {
        yield saveCustomerStateEdge(CHECK_ELIGIBILITY_STEP_ID);

        const response = yield take([
            'api/PUT_CUSTOMER_STATE_EDGE [success]',
            'api/PUT_CUSTOMER_STATE_EDGE [fail]'
        ]);

        if (response.type === 'api/PUT_CUSTOMER_STATE_EDGE [fail]')
            throw new Error('Customer state has failed');

        yield* updateSplitTestAttributes();

        const formValues = yield select(({ form }) => getValues(form?.sign_up));
        const businessType = formValues?.businessType?.company?.type;

        yield put(Tracking.trackLeadQualityAction(CURRENT_STEP_ID));

        Tracking.trackBusinessIntroduction(
            formValues?.businessTypeFields?.company
        );
        Tracking.completeSignupStep(CURRENT_STEP_ID);

        const hasAutoDecision = yield select((state) => {
            return hasAutomatedDecision(state);
        });

        tracking.ampli.identify(undefined, {
            'Business type': businessType,
            'Decision type': hasAutoDecision ? 'auto' : 'manual'
        });

        tracking.ampli.businessDetailsSubmitted({
            ...getAmplitudeDefaultEventProperties()
        });

        yield put(nextStep(CHECK_ELIGIBILITY_STEP_ID));
        yield* navigateToNextPage(CHECK_ELIGIBILITY_STEP_ID);
    });
}

function* navigateToNextPage(nextPage) {
    const urlQueryParams = stringify(getQueryParams());
    const basePath = yield select((state) => getBasePath(state));
    const countryCode = yield select((state) => getCountryCode(state));
    const pathname = convertActiveStepIdToStepPathName(
        nextPage,
        countryCode === 'uk'
    );

    yield put(
        navigateTo(
            `${basePath}/${pathname}${
                urlQueryParams ? `?${urlQueryParams}` : ''
            }`
        )
    );
}
